import {
  EntityStatusAuth,
  EntityStatusDesc,
} from '../../../models/entity.model'
import {
  OrderChangeStatusAction,
  OrderPermission,
  OrderStatus,
} from '../models/order.types'

export const ORDER_STATUSES: EntityStatusDesc<OrderStatus>[] = [
  {
    code: 'DRAFTED',
    name: 'Drafted',
    primary: true,
    default: true,
    tooltip:
      'DOES NOT commit the quantities in stock and is not visible for shipping management',
  },
  {
    code: 'PENDING',
    name: 'Pending',
    primary: true,
    default: true,
    tooltip:
      'It commits the quantities in stock but still awaiting CONFIRMATION in order to be managed',
  },
  {
    code: 'CONFIRMED',
    name: 'Confirmed',
    primary: true,
    default: true,
    tooltip:
      'Order ready to be processed for shipping. If changes are needed, it must be put back to PENDING first',
  },
  {
    code: 'PROCESSING',
    name: 'Processing',
    primary: true,
    tooltip: 'Order accepted by the warehouse. Cannot be modified',
  },
  {
    code: 'PROCESSED',
    name: 'Processed',
    primary: true,
    tooltip:
      'OrdineOrder already picked and awaiting packaging with carrier label printing. Quantities already unloaded from the warehouse',
  },
  {
    code: 'PACKED',
    name: 'Order Packed',
    primary: true,
    tooltip: 'Order ready to be handed over to the carrier',
  },
  {
    code: 'SHIPPED',
    name: 'Shipped',
    primary: true,
    tooltip: 'Order handed over to the carrier',
  },
  {
    code: 'DELIVERED',
    name: 'Delivered',
    primary: true,
    tooltip: 'The carrier delivered this order',
  },
  {
    code: 'PARKED',
    name: 'Received',
    primary: true,
    tooltip: 'Order parked at a pickup point waiting for the customer',
  },
  {
    code: 'HANDED',
    name: 'Handed',
    primary: true,
    tooltip: 'Order delivered by hand to the customer',
  },
  {
    code: 'CANCELED',
    name: 'Canceled',
    primary: false,
    tooltip:
      'This does NOT commit the quantities in stock. The order is permanently cancelled and no further transactions can be made on it',
  },
  {
    code: 'DROPSHIPPED',
    name: 'Dropshipped',
    primary: false,
    tooltip: 'The order is managed externally',
  },
]

export const ORDER_ACTIONS: EntityStatusAuth<
  OrderChangeStatusAction,
  OrderPermission
> = {
  DRAFTED: {
    permissions: [
      'checkAddress',
      'checkRows',
      'deleteOrder',
      'editAlert',
      'editHeader',
      'editPayment',
      'editProducts',
      'editPromotion',
      'copyableRef',
      'saveOrder',
      'showProductQuantities',
    ],
    actions: [
      {
        label: 'Confirm order',
        action: 'confirm',
      },
      {
        label: 'Mark as drafted',
        action: 'pend',
      },
    ],
  },
  PENDING: {
    permissions: [
      'cancelOrder',
      'checkAddress',
      'checkCashOnDelivery',
      'checkRows',
      'dropShip',
      'editAlert',
      'editHeader',
      'editPayment',
      'editProducts',
      'editPromotion',
      'copyableRef',
      'printSummary',
      'saveOrder',
      'showProductQuantities',
      'splitOrder',
    ],
    actions: [
      {
        label: 'Confirm order',
        action: 'confirm',
      },
    ],
  },
  CONFIRMED: {
    permissions: [
      'cancelOrder',
      'checkAddress',
      'checkCashOnDelivery',
      'checkPickingList',
      'checkRows',
      'copyableRef',
      'dropShip',
      'editAlert',
      'editHeader',
      'editPayment',
      'editProducts',
      'editPromotion',
      'printSummary',
      'saveOrder',
      'showProductQuantities',
      'splitOrder',
      'pickOrder',
    ],
    actions: [
      {
        label: 'Mark as drafted',
        action: 'pend',
      },
    ],
  },
  PROCESSING: {
    permissions: [
      'checkCashOnDelivery',
      'checkPickingList',
      'checkPickError',
      'editAlert',
      'editCashOnDelivery',
      'editShipping',
      'packOrder',
      'pickUpProducts',
      'printSummary',
      'resetPicking',
      'saveOrder',
      'showProductQuantities',
    ],
    actions: [],
  },
  PROCESSED: {
    permissions: [
      'cancelOrder',
      'checkCashOnDelivery',
      'checkBordereau',
      'checkPackFinished',
      'editAlert',
      'editCashOnDelivery',
      'editPacking',
      'editShipping',
      'packOrder',
      'printSummary',
      'saveOrder',
      'showPackageInfo',
      'showPickedQuantities',
      'toggleAmountsEditable',
    ],
    actions: [
      {
        label: 'Mark as delivered',
        action: 'deliver',
      },
    ],
  },
  PACKED: {
    permissions: [
      'cancelOrder',
      'cancelShipment',
      'editAlert',
      'printLabel',
      'printSummary',
      'saveOrder',
      'showPackageInfo',
      'showShipmentInfo',
      'checkBordereau',
      'showShippedQuantities',
      'toggleAmountsEditable',
      'editShipping',
      'editPacking',
      'packOrder',
    ],
    actions: [
      {
        label: 'Mark as shipped',
        action: 'ship',
      },
    ],
  },
  SHIPPED: {
    permissions: [
      'editAlert',
      'printSummary',
      'saveOrder',
      'printLabel',
      'showPackageInfo',
      'showShipmentInfo',
      'showShippedQuantities',
      'toggleAmountsEditable',
    ],
    actions: [
      {
        label: 'Mark as delivered',
        action: 'deliver',
      },
      {
        label: 'Mark as received',
        action: 'park',
      },
    ],
  },
  DELIVERED: {
    permissions: [
      'editAlert',
      'printSummary',
      'saveOrder',
      'showPackageInfo',
      'showShipmentInfo',
      'showShippedQuantities',
      'toggleAmountsEditable',
    ],
    actions: [
      {
        label: 'Mark as received',
        action: 'park',
      },
      {
        label: 'Mark as handed',
        action: 'hand',
      },
    ],
  },
  PARKED: {
    permissions: [
      'editAlert',
      'printSummary',
      'saveOrder',
      'showPackageInfo',
      'showShipmentInfo',
      'showShippedQuantities',
      'toggleAmountsEditable',
    ],
    actions: [
      {
        label: 'Mark as handed',
        action: 'hand',
      },
    ],
  },
  HANDED: {
    permissions: [
      'editAlert',
      'printSummary',
      'saveOrder',
      'showPackageInfo',
      'showShipmentInfo',
      'showShippedQuantities',
      'toggleAmountsEditable',
    ],
    actions: [],
  },
  CANCELED: {
    permissions: ['editAlert', 'saveOrder'],
    actions: [],
  },
  DROPSHIPPED: {
    permissions: ['editAlert', 'printSummary', 'saveOrder'],
    actions: [
      {
        label: 'Revert to confirmed',
        action: 'confirm',
      },
    ],
  },
}
